import { gql, useMutation, useQuery } from '@apollo/client'
import Notifications from '@mui/icons-material/Notifications'
import { useTheme } from '@mui/material'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { Mutation, Query } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { makeStyles } from 'tss-react/mui'

import analytics from '@src/analytics'
import useManageBrandDialog from '@src/components/brands/useManageBrandDialog'
import { FacebookPermissionsDialog } from '@src/components/shared/FacebookPermissions/FacebookPermissionsDialog'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

import normalizeAlert, {
  AlertOnClickContext,
  NormalizedAlert,
} from './normalizeAlert'
import Notification from './Notification'

const useStyles = makeStyles()(() => ({
  notificationsContainer: {
    maxHeight: '70vh',
    overflow: 'auto',
  },
}))

const NotificationsDropdown: React.FC<React.PropsWithChildren> = (props) => {
  // General
  const { classes } = useStyles()
  const [currentFacebookPageId, setCurrentFacebookPageId] = useState<
    string | null
  >(null)
  const [isFacebookPermissionsDialogOpen, setIsFacebookPermissionsDialogOpen] =
    useState(false)
  const currentRetailerAccount = useSelector(currentRetailerAccountSelector)

  // GraphQL
  const { data, refetch } = useQuery<Query>(GET_ALERTS, {
    variables: {
      resourceType: 'retailer_accounts',
      resourceId: currentRetailerAccount.id,
      params: {
        include_retailers: true,
        fields: ['subjectable', 'subjectable.campaign', 'subjectable.brand'],
        sort_column: 'created_at',
        sort_direction: 'desc',
      },
    },
  })
  const [markAllAlertsAsRead] = useMutation<Mutation>(MARK_ALL_ALERTS_AS_READ)
  const [updateAlert] = useMutation<Mutation>(UPDATE_ALERT)

  // Alert Context
  const { showManageBrandDialog } = useManageBrandDialog({})
  const history = useHistory()
  const isNewShareFlowEnabled = useFeatureFlag('share-flow-uplift')
  const context: AlertOnClickContext = {
    history,
    showFacebookPermissionsDialog: (id) => {
      if (id) {
        setCurrentFacebookPageId(id)
      }
      setIsFacebookPermissionsDialogOpen(true)
    },
    showManageBrandDialog,
    isNewShareFlowEnabled,
  }

  // Popover
  const [anchorEl, setAnchorEl] = useState<HTMLElement>()
  const open = Boolean(anchorEl)
  const id = open ? 'simple-popper' : undefined
  const theme = useTheme()
  const { alerts, numUnread } =
    useMemo(() => {
      let numUnread = 0
      const groups: Record<string, NormalizedAlert> = {}
      const normalizedAlerts: NormalizedAlert[] = []

      for (const alert of data?.getAlerts || []) {
        const normalized = normalizeAlert(alert, theme)

        if (!normalized) {
          continue
        }

        if (normalized.alert.status === 'unread') {
          numUnread += 1
        }

        const groupKey = normalized.groupKey
        if (groupKey) {
          let group = groups[groupKey]

          if (!group) {
            group = {
              ...normalized,
              alert: { ...normalized.alert },
              message: I18n.t(`alerts.groups.${normalized.groupKey}`),
              children: [normalized],
              badge: () =>
                group?.children?.filter((x) => x.alert.status === 'unread')
                  .length,
            }

            groups[groupKey] = group
            normalizedAlerts.push(group)
          } else {
            group.children?.push(normalized)
          }
          if (normalized.alert.status === 'unread') {
            group.alert.status = 'unread'
          }
        } else {
          normalizedAlerts.push(normalized)
        }
      }

      return {
        alerts: normalizedAlerts,
        numUnread,
      }
    }, [data, theme]) || []

  const markAsRead = async (id: number) => {
    await updateAlert({
      variables: {
        resourceId: id,
        params: {
          status: 'read',
        },
      },
    })
  }

  const markAllAsRead = async () => {
    analytics.track({
      event: 'Click',
      type: 'Alerts Mark All Read',
      data: {
        id: currentRetailerAccount.id,
      },
    })
    await markAllAlertsAsRead({
      variables: {
        resourceId: currentRetailerAccount.id,
      },
    })
    refetch()
    toggleMenu()
  }

  const toggleMenu = (event?: React.MouseEvent<HTMLElement>) => {
    const element = event?.currentTarget
    setAnchorEl(element)
    analytics.track({
      event: 'Click',
      type: 'Alerts Menu Toggle',
      data: {
        showMenu: !!element,
      },
    })
  }

  const renderNotifications = () => {
    if (alerts?.length) {
      return (
        <>
          <List className={classes.notificationsContainer}>
            {alerts.map((alert) => (
              <Notification
                key={alert?.alert.id}
                markAsRead={markAsRead}
                alert={alert!}
                context={context}
              />
            ))}
          </List>
          <Button fullWidth onClick={markAllAsRead} color="secondary">
            {I18n.t('nav.markAllAsRead')}
          </Button>
        </>
      )
    } else {
      return (
        <Box padding={4}>
          <Typography variant="body1" color="textSecondary" align="center">
            {I18n.t('nav.noNotifications')}
          </Typography>
        </Box>
      )
    }
  }

  return (
    <>
      <IconButton onClick={toggleMenu} color="inherit" size="large">
        <Badge badgeContent={numUnread} max={99} color="error">
          <Notifications color="inherit" />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => toggleMenu()}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            width: 320,
          },
        }}
      >
        {renderNotifications()}
      </Popover>
      <FacebookPermissionsDialog
        initialFacebookPageId={currentFacebookPageId}
        open={isFacebookPermissionsDialogOpen}
        handleClose={() => {
          setIsFacebookPermissionsDialogOpen(false)
          setCurrentFacebookPageId(null)
        }}
      />
    </>
  )
}

const GET_ALERTS = gql`
  query getAlerts(
    $params: GetAlertsParams
    $resourceId: ID!
    $resourceType: String!
  ) {
    getAlerts(
      params: $params
      resourceId: $resourceId
      resourceType: $resourceType
    ) {
      id
      alert_type
      category
      status
      created_at
      message
      alertable_id
      alertable_type
      retailer_account {
        id
        name
        slug
      }
      retailer(params: { fields: ["brand"] }) {
        id
        name
        slug
        brand {
          id
          name
          slug
        }
      }
      subjectable {
        ... on RetailerAccount {
          id
          name
          slug
        }
        ... on FacebookPage {
          id
          name
        }
        ... on Retailer {
          id
          name
          slug
          brand {
            id
            name
            slug
          }
        }
        ... on TwitterAccount {
          id
          name
          nickname
        }
        # The linter complains because we're not asking for the ID, but when we
        # ask for the ID in multiple fragments it also complains because their
        # types don't line up.
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        ... on EmailShare {
          campaign {
            id
            link_id
            name
          }
          brand {
            id
            slug
          }
        }
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        ... on FacebookShare {
          share_setting_id
          campaign {
            id
            link_id
            name
          }
          brand {
            id
            slug
          }
        }
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        ... on InstagramShare {
          share_setting_id
          campaign {
            id
            link_id
            name
          }
          brand {
            id
            slug
          }
        }
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        ... on TwitterShare {
          share_setting_id
          campaign {
            id
            link_id
            name
          }
          brand {
            id
            slug
          }
        }
      }
      subjectable_type
      subjectable_id
    }
  }
`

const MARK_ALL_ALERTS_AS_READ = gql`
  mutation markAllAlertsAsRead($resourceId: ID!) {
    markAllAlertsAsRead(resourceId: $resourceId, include_retailers: true)
  }
`

export const UPDATE_ALERT = gql`
  mutation updateAlert($resourceId: ID!, $params: UpdateAlertParams!) {
    updateAlert(resourceId: $resourceId, params: $params) {
      id
      status
    }
  }
`

export default NotificationsDropdown
